import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { endOfDay, format, startOfDay, startOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { fetchAnalytics, fetchCampaign } from '@/services/campaign';
import { CampaignNotFound } from '@/components/not-found/campaign-not-found';
import { Loading } from '@/components/loader/loading';
import i18next from '@/i18next';
import { Badge } from '@/components/ui/badge';
import { HeroKpi } from '@/components/hero-kpi';
import { KpiStrip } from '@/components/kpi-strip';
import { Leaderboard } from '@/components/leaderboard';
import { BreakdownBars } from '@/components/breakdown-bars';
import { InsightsPanel } from '@/components/insights-panel';
import { AnalyticNotFound } from '@/components/not-found/analytic-not-found';
import { ScanOverTimeLine } from '@/routes/console/_authenticated/_layout/-analytics/scan-over-time-line';
import { DATE_FORMAT_ISO } from '@/constants/date';
import { EntryPointSelector } from '@/components/selector/entry-point-selector';
import { AnalyticsFilter } from '@/components/analytics-filter';
import { FiltersRow } from '@/components/filter-row';
import { computeCampaignInsights, halfPeriodDelta } from '@/helpers/analytics-insights.helper';
import { paletteFor } from '@/helpers/breakdown-palette.helper';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';
const searchSchema = z.object({
    from: z.string().default(format(startOfMonth(new Date()), DATE_FORMAT_ISO)),
    to: z.string().default(format(new Date(), DATE_FORMAT_ISO)),
    entryPoint: z.string().optional(),
    excludeDevices: z.array(z.string()).default(['bot']),
    excludeBrowsers: z.array(z.string()).default(['bot']),
});
const DEVICE_LABELS = {
    iphone: 'iPhone',
    android: 'Android',
    mobile: 'Mobile',
    ipad: 'iPad',
    tablet: 'Tablet',
    desktop: 'Desktop',
    macintosh: 'Macintosh',
    bot: 'Bot',
    unknown: 'Unknown',
    other: 'Other',
    tv: 'TV',
};
const BROWSER_LABELS = {
    chrome: 'Chrome',
    safari: 'Safari',
    firefox: 'Firefox',
    edge: 'Edge',
    opera: 'Opera',
    facebook: 'Facebook',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    twitter: 'Twitter',
    linkedin: 'LinkedIn',
    reddit: 'Reddit',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    messenger: 'Messenger',
    pinterest: 'Pinterest',
    bot: 'Bot',
    unknown: 'Other',
};
export const Route = createFileRoute('/console/_authenticated/_layout/campaigns/$publicId/_layout/analytics/')({
    component: AnalyticPage,
    loader: ({ params: { publicId } }) => fetchCampaign(publicId),
    validateSearch: zodValidator(searchSchema),
    errorComponent: () => <CampaignNotFound />,
    pendingComponent: () => <Loading loadingText={i18next.t('campaigns.loading.campaign', { ns: 'glossary' })}/>,
});
function AnalyticPage() {
    const campaign = Route.useLoaderData();
    const { t } = useTranslation();
    const { from, to, entryPoint, excludeDevices, excludeBrowsers } = Route.useSearch();
    const navigate = Route.useNavigate();
    const fromDate = React.useMemo(() => new Date(from), [from]);
    const toDate = React.useMemo(() => new Date(to), [to]);
    const { data: analytics, isPending } = useQuery({
        queryKey: ['campaign-analytics', campaign.publicId, from, to, entryPoint, excludeDevices, excludeBrowsers],
        queryFn: () => fetchAnalytics({
            publicId: campaign.publicId,
            from: new Date(from),
            to: new Date(to),
            entryPoint: entryPoint === 'all' ? undefined : entryPoint,
            excludeDevices,
            excludeBrowsers,
        }),
        enabled: !!from && !!to,
    });
    const handleRangeChange = React.useCallback((newFrom, newTo) => {
        navigate({
            search: {
                entryPoint,
                from: format(startOfDay(newFrom), DATE_FORMAT_ISO),
                to: format(endOfDay(newTo), DATE_FORMAT_ISO),
                excludeDevices,
                excludeBrowsers,
            },
        });
    }, [entryPoint, excludeBrowsers, excludeDevices, navigate]);
    const handleEntryPointChange = React.useCallback((ep) => {
        navigate({ search: { entryPoint: ep, from, to, excludeDevices, excludeBrowsers } });
    }, [from, navigate, to, excludeDevices, excludeBrowsers]);
    const handleDevicesChange = React.useCallback((values) => {
        navigate({ search: { entryPoint, from, to, excludeDevices: values, excludeBrowsers } });
    }, [entryPoint, from, to, excludeBrowsers, navigate]);
    const handleBrowsersChange = React.useCallback((values) => {
        navigate({ search: { entryPoint, from, to, excludeDevices, excludeBrowsers: values } });
    }, [entryPoint, from, to, excludeDevices, navigate]);
    if (isPending) {
        return <Loading loadingText={t('campaigns.loading.analytic', { ns: 'glossary' })}/>;
    }
    if (!analytics) {
        return <AnalyticNotFound />;
    }
    const scansDelta = halfPeriodDelta(analytics.timelines.map((p) => p.scans));
    const sparkData = analytics.timelines.map((p) => ({ date: p.date, value: p.scans }));
    const trackingLinkRows = analytics.trackingLinks
        .slice()
        .sort((a, b) => b.scans - a.scans)
        .map((link, idx) => ({
        key: `${link.label}-${idx}`,
        label: link.label,
        value: link.scans,
        secondary: link.targetUrl,
        barClassName: paletteFor(idx),
    }));
    const destinationRows = analytics.destinations
        .slice()
        .sort((a, b) => b.scans - a.scans)
        .map((d, idx) => ({
        key: `dest-${d.label}-${idx}`,
        label: d.label,
        value: d.scans,
        barClassName: paletteFor(idx),
    }));
    const deviceEntries = [...analytics.devices]
        .sort((a, b) => b.scans - a.scans)
        .map((d, idx) => ({
        key: d.deviceType,
        label: DEVICE_LABELS[d.deviceType] ?? d.deviceType,
        value: d.scans,
        barClassName: paletteFor(idx),
    }));
    const browserEntries = [...analytics.browsers]
        .sort((a, b) => b.scans - a.scans)
        .map((b, idx) => ({
        key: b.browserType,
        label: BROWSER_LABELS[b.browserType] ?? b.browserType,
        value: b.scans,
        barClassName: paletteFor(idx),
    }));
    const insights = computeCampaignInsights({
        kpis: analytics.kpis,
        timelines: analytics.timelines,
        devices: analytics.devices,
        browsers: analytics.browsers,
    });
    const periodLabel = `${format(fromDate, 'MMM d')} – ${format(toDate, 'MMM d, yyyy')}`;
    return (<div className="flex flex-col gap-6 p-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="space-y-1">
                    <h1 className="text-2xl font-semibold tracking-tight">
                        {t('campaigns.analytics.title', { ns: 'glossary', name: campaign.name })}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="font-mono text-xs">
                            ad-{campaign.publicId}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                            {t('campaigns.analytics.caption', { ns: 'glossary' })}
                        </span>
                    </div>
                </div>
                <span className="rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs text-muted-foreground tabular-nums">
                    {periodLabel}
                </span>
            </div>

            <div className="sticky top-0 z-10 -mx-6 -my-1 rounded-xl border border-border/60 bg-card/70 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-card/50">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <FiltersRow from={fromDate} to={toDate} setRange={handleRangeChange}/>
                    <div className="flex flex-wrap items-end gap-3">
                        <EntryPointSelector label={t('campaigns.analytics.entry_point', { ns: 'glossary' })} campaign={campaign} value={entryPoint} onChange={handleEntryPointChange}/>
                        <AnalyticsFilter excludeDevices={excludeDevices} excludeBrowsers={excludeBrowsers} onDevicesChange={handleDevicesChange} onBrowsersChange={handleBrowsersChange}/>
                    </div>
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                <HeroKpi className="lg:col-span-2" label={t('analytics.kpi.total_scans', { ns: 'campaign' })} value={analytics.kpis.totalScans.toLocaleString()} sublabel={analytics.kpis.totalScans > 0
            ? t('scans_this_period', { defaultValue: 'scans this period' })
            : undefined} delta={scansDelta} deltaLabel={scansDelta !== null
            ? t('vs_previous_period', { ns: 'glossary', defaultValue: 'vs. previous period' })
            : undefined} spark={sparkData}/>
                <InsightsPanel insights={insights}/>
            </div>

            <KpiStrip items={[
            {
                label: t('analytics.kpi.unique_visitors', { ns: 'campaign' }),
                value: analytics.kpis.uniqueVisitors.toLocaleString(),
                hint: analytics.kpis.totalScans > 0 && analytics.kpis.uniqueVisitors > 0
                    ? `${(analytics.kpis.totalScans / analytics.kpis.uniqueVisitors).toFixed(1)}× scans / visitor`
                    : undefined,
            },
            {
                label: t('analytics.kpi.best_link', { ns: 'campaign' }),
                value: analytics.kpis.topLink?.label ?? '—',
                hint: analytics.kpis.topLink ? `${analytics.kpis.topLink.share}% share` : undefined,
            },
            {
                label: t('analytics.kpi.avg_scans_per_day', { ns: 'campaign' }),
                value: analytics.kpis.avgDailyScans.toLocaleString(),
            },
            {
                label: t('analytics.kpi.destinations', { defaultValue: 'Destinations' }),
                value: analytics.destinations.length.toLocaleString(),
                hint: analytics.trackingLinks.length > 0
                    ? `${analytics.trackingLinks.length} tracking links`
                    : undefined,
            },
        ]}/>

            <ScanOverTimeLine timelines={analytics.timelines}/>

            <div className="grid gap-4 lg:grid-cols-2">
                <Leaderboard title={t('analytics.tlp.title', { ns: 'glossary', defaultValue: 'Tracking link performance' })} description={t('analytics.tlp.description', {
            ns: 'glossary',
            defaultValue: 'Ranked by scans, including share of the campaign total.',
        })} rows={trackingLinkRows} valueLabel={t('scans', { ns: 'common', defaultValue: 'Scans' })}/>
                <Leaderboard title={t('analytics.da.title', { ns: 'glossary', defaultValue: 'Destinations' })} description={t('analytics.da.description', {
            ns: 'glossary',
            defaultValue: 'Where visitors actually went after scanning.',
        })} rows={destinationRows} valueLabel={t('scans', { ns: 'common', defaultValue: 'Scans' })}/>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <BreakdownBars title={t('analytics.dt.title', { ns: 'glossary' })} description={t('analytics.dt.description', { ns: 'glossary' })} entries={deviceEntries}/>
                <BreakdownBars title={t('analytics.bt.title', { ns: 'glossary' })} description={t('analytics.bt.description', { ns: 'glossary' })} entries={browserEntries}/>
            </div>
        </div>);
}
